/**
 * General Helpers.
 * 
 * Handles a lot of utility work.
 */

/**
 * Gets the value of a css variable on an element.
 */
export function getCssVar( variable, element = null ) {

    element = element || document.body

    return window.getComputedStyle( element )
        .getPropertyValue( `--${variable}` )
}

/**
 * Gets all internal anchor links.
 */
export function getInternalLinks( includeAnchors = false ) {

    const protocols = ['http', 'https']
    const hostName =  top.location.host.toString()

    const selectors = [
        'a[href^="/"], a[href^="./"], a[href^=".."]' + ( includeAnchors ? ', a[href^="#"]' : '' )
    ];

    for ( const protocol of protocols ) {
        selectors.push( `a[href^="${protocol}://${hostName}"]` )
    }

    const nodeList = nodeListDiff(
        document.querySelectorAll( selectors.join( ', ' ) ),
        document.querySelectorAll( 'a[target="_blank"]' )
    )

    return nodeList
}

/**
 * Returns an array of elements from a nodeList only if they are not present in any others.
 */
export function nodeListDiff( nodeList, ...comparedNodeLists ) {

    const diff = [];

    for ( const node of nodeList ) {

        let inComparedNodeLists = false

        comparedListsLoop: for ( const comparedNodeList of comparedNodeLists ) {
            for ( const comparedNode of comparedNodeList ) {
                if ( node.isEqualNode( comparedNode ) ) {
                    inComparedNodeLists = true
                    break comparedListsLoop
                }
            }
        }

        if ( ! inComparedNodeLists ) {
            diff.push( node )
        }
    }

    return diff
}

/**
 * Determines if a value is numeric.
 */
export function isNumeric( value ) {

    switch ( typeof value ) {
        case 'number':
            return true
        case 'string':
            return !! ( +value === +value || value.match( /^[0-9,]*$/g ) )
        default:
            return false
    }
}

/**
 * Gets a number or numeric string's precision.
 */
export function getNumberPrecision( number ) {

    let p = 0

    if ( typeof number === 'string' ) {
        const decimals = number.split( '.' )[1] || ''
        p = decimals.length
    }

    else {

        if ( ! isFinite( number ) ) return 0

        let e = 1

        while ( Math.round( number * e ) / e !== number ) {
            e *= 10
            p++
        }
    }

    return p;
}

/**
 * Capitalizes the first letter of a string.
 * 
 * Thanks, Steve Harrison!
 * 
 * @link https://stackoverflow.com/a/1026087
 */
export function ucFirst( string ) {
    return string.charAt( 0 ).toUpperCase() + string.slice( 1 )
}

/**
 * Determines if a value is a JS object.
 */
export function isObject( value ) {
    return typeof value === 'object' && ! Array.isArray( value ) && value !== null
}

/**
 * Filters an object, similar to an array filter.
 * By default, removes all entries with falsy values.
 */
export function filterObject( object, callback = null ) {

    callback = callback || ( ( value, key ) => !! value )

    return Object.fromEntries(
        Object.entries( object ).filter( ( [key, value] ) => callback( value, key ) )
    )
}

/**
 * Runs a callback function once for each entry in an object.
 * Similar to array mapping.
 * 
 * Thanks, Amberlamps!
 * 
 * @link https://stackoverflow.com/a/14810722
 */
export function mapObject( object, callback ) {
    return Object.fromEntries(
        Object.entries( object ).map(
            ( [key, value], index ) => [ key, callback( value, key, index )]
        )
    )
}

/**
 * Computes the difference between arrays.
 */
export function diffArray( array1, array2 ) {
    return array1.filter( ( value ) => ! array2.includes( value ) )
}
